import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, Target, Bell, Plus, CheckCircle, Clock, AlertCircle, 
  Send, Edit, Trash2, Calendar, DollarSign, Loader2, ChevronRight
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function HighTicketProjects() {
  const [orders, setOrders] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [milestones, setMilestones] = useState([]);
  
  // Dialogs
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  
  // Forms
  const [contractForm, setContractForm] = useState({ title: '', content: '', terms: [] });
  const [milestoneForm, setMilestoneForm] = useState([
    { title: '', description: '', due_date: '', amount: 0, deliverables: [] }
  ]);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, contractsRes, remindersRes] = await Promise.all([
        api.get('/tenant/orders?type=high_ticket'),
        api.get('/tenant/high-ticket/contracts'),
        api.get('/tenant/high-ticket/reminders')
      ]);
      setOrders(ordersRes.data.orders || []);
      setContracts(contractsRes.data.contracts || []);
      setReminders(remindersRes.data.reminders || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async (orderId) => {
    try {
      const res = await api.get(`/tenant/high-ticket/orders/${orderId}/milestones`);
      setMilestones(res.data.milestones || []);
    } catch (err) {
      console.error('Failed to fetch milestones:', err);
    }
  };

  const createContract = async () => {
    if (!selectedOrder) return;
    try {
      await api.post('/tenant/high-ticket/contracts', {
        order_id: selectedOrder.id,
        ...contractForm
      });
      toast({ title: 'Berhasil', description: 'Kontrak berhasil dibuat' });
      setShowContractDialog(false);
      setContractForm({ title: '', content: '', terms: [] });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal membuat kontrak', variant: 'destructive' });
    }
  };

  const sendContract = async (contractId) => {
    try {
      await api.put(`/tenant/high-ticket/contracts/${contractId}/send`);
      toast({ title: 'Berhasil', description: 'Kontrak berhasil dikirim ke customer' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal mengirim kontrak', variant: 'destructive' });
    }
  };

  const signContract = async (contractId) => {
    try {
      await api.put(`/tenant/high-ticket/contracts/${contractId}/sign`);
      toast({ title: 'Berhasil', description: 'Kontrak berhasil ditandatangani' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menandatangani kontrak', variant: 'destructive' });
    }
  };

  const createMilestones = async () => {
    if (!selectedOrder) return;
    try {
      await api.post('/tenant/high-ticket/milestones', {
        order_id: selectedOrder.id,
        milestones: milestoneForm.filter(m => m.title)
      });
      toast({ title: 'Berhasil', description: 'Milestone berhasil dibuat' });
      setShowMilestoneDialog(false);
      setMilestoneForm([{ title: '', description: '', due_date: '', amount: 0, deliverables: [] }]);
      fetchMilestones(selectedOrder.id);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal membuat milestone', variant: 'destructive' });
    }
  };

  const updateMilestoneStatus = async (milestoneId, status) => {
    try {
      await api.put(`/tenant/high-ticket/milestones/${milestoneId}/status?status=${status}`);
      toast({ title: 'Berhasil', description: 'Status milestone diupdate' });
      if (selectedOrder) fetchMilestones(selectedOrder.id);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal update status', variant: 'destructive' });
    }
  };

  const sendReminders = async () => {
    try {
      const res = await api.post('/tenant/high-ticket/send-reminders');
      toast({ 
        title: 'Berhasil', 
        description: `${res.data.total_sent} reminder terkirim` 
      });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal mengirim reminder', variant: 'destructive' });
    }
  };

  const addMilestoneRow = () => {
    setMilestoneForm([...milestoneForm, { title: '', description: '', due_date: '', amount: 0, deliverables: [] }]);
  };

  const updateMilestoneRow = (index, field, value) => {
    const newForm = [...milestoneForm];
    newForm[index] = { ...newForm[index], [field]: value };
    setMilestoneForm(newForm);
  };

  const removeMilestoneRow = (index) => {
    setMilestoneForm(milestoneForm.filter((_, i) => i !== index));
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const getStatusBadge = (status) => {
    const map = {
      'draft': { label: 'Draft', className: 'bg-slate-500' },
      'sent': { label: 'Terkirim', className: 'bg-blue-500' },
      'completed': { label: 'Selesai', className: 'bg-emerald-500' },
      'pending': { label: 'Pending', className: 'bg-amber-500' },
      'in_progress': { label: 'Berjalan', className: 'bg-blue-500' },
      'review': { label: 'Review', className: 'bg-purple-500' },
      'cancelled': { label: 'Batal', className: 'bg-red-500' }
    };
    const s = map[status] || map.pending;
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="high-ticket-projects-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">High Ticket Projects</h1>
          <p className="text-muted-foreground">Kelola kontrak, milestone, dan reminder untuk order high-ticket</p>
        </div>
        <Button onClick={sendReminders} variant="outline">
          <Bell className="h-4 w-4 mr-2" /> Kirim Reminder
        </Button>
      </div>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="contracts">Kontrak ({contracts.length})</TabsTrigger>
          <TabsTrigger value="reminders">Reminders ({reminders.length})</TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Order List */}
            <Card>
              <CardHeader>
                <CardTitle>Daftar Order High-Ticket</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders.map((order) => (
                    <button
                      key={order.id}
                      onClick={() => {
                        setSelectedOrder(order);
                        fetchMilestones(order.id);
                      }}
                      className={`w-full p-4 border rounded-lg text-left transition-all ${
                        selectedOrder?.id === order.id ? 'border-[#2D6CDF] bg-blue-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{order.order_number || order.id.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">Rp {fmt(order.total_amount)}</p>
                          {getStatusBadge(order.status)}
                        </div>
                      </div>
                      {order.has_milestones && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Milestone: {order.milestones_completed || 0}/{order.milestone_count || 0}
                        </div>
                      )}
                    </button>
                  ))}
                  {orders.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Belum ada order high-ticket</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Detail & Milestones */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Detail & Milestone</CardTitle>
                  {selectedOrder && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setShowContractDialog(true)}>
                        <FileText className="h-4 w-4 mr-1" /> Kontrak
                      </Button>
                      <Button size="sm" onClick={() => setShowMilestoneDialog(true)}>
                        <Target className="h-4 w-4 mr-1" /> Milestone
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedOrder ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="font-medium">{selectedOrder.customer_name}</p>
                      <p className="text-2xl font-bold text-[#2D6CDF]">Rp {fmt(selectedOrder.total_amount)}</p>
                    </div>

                    {/* Milestones */}
                    <div>
                      <h4 className="font-medium mb-3">Milestone Progress</h4>
                      {milestones.length > 0 ? (
                        <div className="space-y-3">
                          {milestones.map((m, i) => (
                            <div key={m.id} className="p-3 border rounded-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                    m.status === 'completed' ? 'bg-emerald-500' : 'bg-slate-400'
                                  }`}>
                                    {m.status === 'completed' ? <CheckCircle className="h-4 w-4" /> : i + 1}
                                  </div>
                                  <div>
                                    <p className="font-medium">{m.title}</p>
                                    <p className="text-sm text-muted-foreground">{m.description}</p>
                                  </div>
                                </div>
                                {getStatusBadge(m.status)}
                              </div>
                              <div className="flex items-center justify-between mt-3 pt-3 border-t text-sm">
                                <div className="flex items-center gap-4 text-muted-foreground">
                                  {m.due_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" /> {m.due_date.split('T')[0]}
                                    </span>
                                  )}
                                  {m.amount > 0 && (
                                    <span className="flex items-center gap-1">
                                      <DollarSign className="h-3 w-3" /> Rp {fmt(m.amount)}
                                    </span>
                                  )}
                                </div>
                                <Select 
                                  value={m.status}
                                  onValueChange={(v) => updateMilestoneStatus(m.id, v)}
                                >
                                  <SelectTrigger className="w-32 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="review">Review</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-4">
                          Belum ada milestone. Klik "Milestone" untuk membuat.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Pilih order untuk melihat detail
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts">
          <Card>
            <CardHeader>
              <CardTitle>Daftar Kontrak Digital</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Judul</th>
                      <th className="text-left py-3 px-4">Order</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Tanda Tangan</th>
                      <th className="text-left py-3 px-4">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => (
                      <tr key={contract.id} className="border-b">
                        <td className="py-3 px-4">{contract.title}</td>
                        <td className="py-3 px-4 font-mono text-sm">{contract.order_id?.slice(0, 8)}</td>
                        <td className="py-3 px-4">{getStatusBadge(contract.status)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {contract.tenant_signed ? (
                              <Badge className="bg-emerald-500">Tenant ✓</Badge>
                            ) : (
                              <Badge variant="outline">Tenant</Badge>
                            )}
                            {contract.customer_signed ? (
                              <Badge className="bg-emerald-500">Customer ✓</Badge>
                            ) : (
                              <Badge variant="outline">Customer</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            {contract.status === 'draft' && (
                              <Button size="sm" variant="outline" onClick={() => sendContract(contract.id)}>
                                <Send className="h-3 w-3 mr-1" /> Kirim
                              </Button>
                            )}
                            {!contract.tenant_signed && (
                              <Button size="sm" onClick={() => signContract(contract.id)}>
                                <Edit className="h-3 w-3 mr-1" /> Tanda Tangani
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {contracts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-muted-foreground">
                          Belum ada kontrak
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders">
          <Card>
            <CardHeader>
              <CardTitle>Riwayat Reminder</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <div key={reminder.id} className="p-4 border rounded-lg flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Bell className={`h-4 w-4 ${reminder.type === 'overdue' ? 'text-red-500' : 'text-amber-500'}`} />
                        <Badge className={reminder.type === 'overdue' ? 'bg-red-500' : 'bg-amber-500'}>
                          {reminder.type === 'overdue' ? 'Overdue' : 'Upcoming'}
                        </Badge>
                      </div>
                      <p className="text-sm mt-1">
                        Termin Rp {fmt(reminder.amount)} - Jatuh tempo: {reminder.due_date?.split('T')[0]}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {reminder.customer_phone || reminder.customer_email}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(reminder.sent_at).toLocaleString('id-ID')}
                    </p>
                  </div>
                ))}
                {reminders.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">Belum ada reminder terkirim</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Contract Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Buat Kontrak Digital</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Judul Kontrak</Label>
              <Input 
                value={contractForm.title}
                onChange={(e) => setContractForm({ ...contractForm, title: e.target.value })}
                placeholder="Kontrak Kerjasama Pembangunan Website"
              />
            </div>
            <div>
              <Label>Isi Kontrak</Label>
              <Textarea 
                value={contractForm.content}
                onChange={(e) => setContractForm({ ...contractForm, content: e.target.value })}
                placeholder="Tuliskan isi kontrak di sini..."
                rows={10}
              />
            </div>
            <div>
              <Label>Syarat & Ketentuan (satu per baris)</Label>
              <Textarea 
                value={contractForm.terms.join('\n')}
                onChange={(e) => setContractForm({ ...contractForm, terms: e.target.value.split('\n').filter(Boolean) })}
                placeholder="1. Pembayaran dilakukan sesuai termin&#10;2. Perubahan scope akan dikenakan biaya tambahan&#10;3. ..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContractDialog(false)}>Batal</Button>
            <Button onClick={createContract}>Buat Kontrak</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Milestone Dialog */}
      <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Buat Milestone Proyek</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {milestoneForm.map((m, i) => (
              <div key={i} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Milestone {i + 1}</p>
                  {milestoneForm.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeMilestoneRow(i)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Judul</Label>
                    <Input 
                      value={m.title}
                      onChange={(e) => updateMilestoneRow(i, 'title', e.target.value)}
                      placeholder="Desain UI/UX"
                    />
                  </div>
                  <div>
                    <Label>Tanggal Target</Label>
                    <Input 
                      type="date"
                      value={m.due_date}
                      onChange={(e) => updateMilestoneRow(i, 'due_date', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Deskripsi</Label>
                  <Textarea 
                    value={m.description}
                    onChange={(e) => updateMilestoneRow(i, 'description', e.target.value)}
                    placeholder="Deskripsi milestone..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Nominal Pembayaran (Rp)</Label>
                  <Input 
                    type="number"
                    value={m.amount}
                    onChange={(e) => updateMilestoneRow(i, 'amount', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addMilestoneRow} className="w-full">
              <Plus className="h-4 w-4 mr-2" /> Tambah Milestone
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMilestoneDialog(false)}>Batal</Button>
            <Button onClick={createMilestones}>Simpan Milestone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
